// Lists every row in the email_providers table. Each row links to
// /settings/email-providers/:id for edit. The seeded list (~60 rows)
// is sorted by sort_order; tier-1 providers like Gmail and iCloud
// land at the top with their inbox URLs and instructions filled in.
//
// The "fallback" tag marks the provider whose instructions are
// used by the welcome email when the recipient is on an unknown
// or custom domain. That selection lives on the Welcome Email
// settings page; clicking through here doesn't change it.

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Inbox, Trash2, MoreHorizontal } from 'lucide-react';
import { emailProvidersApi, siteSettingsApi, ApiError } from '../lib/api';
import type { EmailProviderListItem } from '@shared/types';

export default function EmailProvidersListPage() {
  const [providers, setProviders] = useState<EmailProviderListItem[] | null>(null);
  const [fallbackId, setFallbackId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const navigate = useNavigate();

  async function load() {
    try {
      setErr(null);
      const [{ providers }, { settings }] = await Promise.all([
        emailProvidersApi.list(),
        siteSettingsApi.getWelcomeEmail(),
      ]);
      setProviders(providers);
      setFallbackId(settings.default_fallback_provider_id);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  return (
    <div>
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <Inbox size={18} className="text-slate-600" />
            <h2 className="text-lg font-medium text-slate-900">Email providers</h2>
          </div>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">
            Powers the post-signup confirmation page CTA ("Open Gmail",
            "Open Outlook", etc.) and the per-provider safelist
            instructions in the welcome email. Domain-to-provider
            matching is exact; vanity / custom domains hit the fallback,
            chosen on{' '}
            <Link to="/settings/welcome-email" className="text-blue-700 underline">
              Welcome Email
            </Link>
            .
          </p>
        </div>
        <button
          onClick={() => navigate('/settings/email-providers/new')}
          className="btn-primary self-start sm:self-auto"
        >
          <Plus size={14} /> New provider
        </button>
      </header>

      {err && <p className="text-sm text-red-600 mb-4">{err}</p>}

      {providers === null ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : providers.length === 0 ? (
        <p className="text-sm text-slate-400">No providers configured.</p>
      ) : (
        <ul className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100">
          {providers.map((p) => (
            <Row
              key={p.id}
              provider={p}
              isFallback={p.id === fallbackId}
              onChange={load}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function Row({
  provider,
  isFallback,
  onChange,
}: {
  provider: EmailProviderListItem;
  isFallback: boolean;
  onChange: () => Promise<void>;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  async function del(e: React.MouseEvent) {
    e.stopPropagation();
    if (
      !confirm(
        `Delete provider "${provider.name}"? Subscribers on these domains will get the fallback welcome-email block instead.`,
      )
    )
      return;
    setBusy(true);
    try {
      await emailProvidersApi.delete(provider.id);
      await onChange();
    } catch (e) {
      if (e instanceof ApiError) {
        alert(e.message);
      } else {
        alert(e instanceof Error ? e.message : 'Failed');
      }
    } finally {
      setBusy(false);
      setMenuOpen(false);
    }
  }

  return (
    <li className="relative group">
      <button
        type="button"
        onClick={() => navigate(`/settings/email-providers/${provider.id}`)}
        disabled={busy}
        className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-slate-50 disabled:opacity-50"
      >
        <Inbox
          size={16}
          className={
            provider.has_inbox_urls
              ? 'text-blue-700 flex-shrink-0'
              : 'text-slate-300 flex-shrink-0'
          }
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`text-sm font-medium truncate ${
                provider.active ? 'text-slate-900' : 'text-slate-400 line-through'
              }`}
            >
              {provider.name}
            </span>
            {isFallback && (
              <span className="text-[10px] uppercase tracking-wide font-semibold bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded">
                fallback
              </span>
            )}
            {!provider.active && (
              <span className="text-[10px] uppercase tracking-wide font-semibold bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">
                inactive
              </span>
            )}
            {!provider.has_inbox_urls && (
              <span className="text-[10px] uppercase tracking-wide font-semibold bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                no URLs
              </span>
            )}
            {!provider.has_instructions && (
              <span className="text-[10px] uppercase tracking-wide font-semibold bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                no instructions
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {provider.domain_count} domain{provider.domain_count === 1 ? '' : 's'}
          </div>
        </div>
        <span
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setMenuOpen((o) => !o);
          }}
          className="p-1.5 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 cursor-pointer"
        >
          <MoreHorizontal size={14} />
        </span>
      </button>
      {menuOpen && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setMenuOpen(false)} />
          <div className="absolute right-3 top-12 z-30 bg-white border border-slate-200 rounded-md shadow-lg py-1 w-44">
            <button
              onClick={del}
              className="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
            >
              <Trash2 size={12} /> Delete
            </button>
          </div>
        </>
      )}
    </li>
  );
}
